import { logger } from '@gem/modules/logger';

import { ICacheConfig } from './cache.types';

export abstract class BaseCache {
  protected db: IDBDatabase;
  protected openRequest: IDBOpenDBRequest;

  protected abstract dbName: string;
  protected abstract initDb(): void;

  private appVersion: string;

  constructor(config: ICacheConfig) {
    this.appVersion = config.appVersion;
  }

  protected getAll<T = any>(storeName: string): Promise<Record<string, T> | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.createTransaction(storeName);

      if (!transaction) {
        resolve(null);
      } else {
        const request = transaction.openCursor();
        const result: Record<string, any> = {};

        request.onsuccess = function (event): void {
          const cursor = (event.target as IDBRequest)?.result as IDBCursorWithValue;

          if (cursor) {
            result[cursor.primaryKey as string] = cursor.value;
            cursor.continue();
          } else {
            resolve(result);
          }
        };
        request.onerror = reject;
      }
    });
  }

  protected get(storeName: string, id: string): Promise<any | null> {
    return new Promise((resolve, reject) => {
      const transaction = this.createTransaction(storeName);

      if (!transaction) {
        resolve(null);
      } else {
        const request = transaction.get(id);

        request.onsuccess = (event): void => {
          const data = (event.target as IDBRequest).result;
          resolve(data);
        };

        request.onerror = reject;
      }
    });
  }

  protected put(storeName: string, id: string, data: any): IDBRequest<IDBValidKey> | null {
    return this.createTransaction(storeName, 'readwrite')?.put(data, id) || null;
  }

  protected getDbVersion(): number {
    const [major, minor, patch] = this.appVersion.split('.');
    const paddedMinor = minor.padStart(3, '0');
    const paddedPatch = patch.padStart(3, '0');
    return parseInt(`${major}${paddedMinor}${paddedPatch}`);
  }

  protected openDb(): void {
    this.openRequest = indexedDB.open(this.dbName, this.getDbVersion());
    this.openRequest.onupgradeneeded = this.onUpgradeNeeded.bind(this);
    this.openRequest.onsuccess = this.onOpenSuccess.bind(this);
    this.openRequest.onerror = this.onOpenError.bind(this);
  }

  protected onUpgradeNeeded(event: IDBVersionChangeEvent): void {
    this.db = (event.target as IDBOpenDBRequest).result;
    this.initDb();
  }

  protected onOpenSuccess(event: Event): void {
    this.db = (event.target as IDBOpenDBRequest).result;
  }

  protected onOpenError(event: Event): void {
    const { error } = event.target as IDBOpenDBRequest;

    if (error?.name === 'VersionError') {
      logger.get('Cache').warn(this.constructor.name, 'VersionError detected. Drop database!');

      this.deleteDb();
      return;
    }

    logger.get('Cache').error(this.constructor.name, error);
  }

  protected createTransaction(
    tableName: string,
    mode: IDBTransactionMode = 'readonly'
  ): IDBObjectStore | null {
    if (!this.db) {
      return null;
    }

    const transaction = this.db.transaction(tableName, mode);
    return transaction.objectStore(tableName);
  }

  protected createStore(storeName: string): void {
    if (!this.db.objectStoreNames.contains(storeName)) {
      this.db.createObjectStore(storeName);
    }
  }

  protected deleteDb(): void {
    if (this.db) {
      this.db.close();
    }

    const deleteRequest = indexedDB.deleteDatabase(this.dbName);
    deleteRequest.onsuccess = this.onDeleteSuccess.bind(this);
    deleteRequest.onerror = this.onDeleteError.bind(this);
    deleteRequest.onblocked = this.onDeleteBlocked.bind(this);
  }

  protected onDeleteSuccess(): void {
    logger.get('Cache').info(this.constructor.name, 'Database dropped successfully');
    this.openDb();
  }

  protected onDeleteError(event: Event): void {
    logger.get('Cache').error(this.constructor.name, 'Failed to drop database', event);
  }

  protected onDeleteBlocked(event: Event): void {
    logger.get('Cache').warn(this.constructor.name, 'Database deletion blocked', event);
  }
}
